import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";
import { NotificationEnum } from "@graphql/generated/graphql.g";
import { useNotificationsQuery } from "@graphql/notifications/Notifications.g";
import { useAutotaskConnectionQuery } from "@graphql/integrations/integrations.g";
import { useNotification } from "@providers/NotificationProvider/useNotification";

import { SlideInPanel } from "@design-system/SlideInPanel/SlideInPanel";
import { Box } from "@design-system/Box/Box";
import { Text } from "@design-system/Text/Text";
import { NotificationTypeMappingText } from "@components/Texts";

import { SaasDiscoveredDetails } from "./components/SaasDiscoveredDetails";
import { SharedLoginDetails } from "./components/SharedLoginDetails";
import { FileUploadDetails } from "./components/FileUploadDetails";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const detailsMap: Record<NotificationEnum, React.FC<any>> = {
  [NotificationEnum.SaasDiscovered]: SaasDiscoveredDetails,
  [NotificationEnum.SharedLogin]: SharedLoginDetails,
  [NotificationEnum.FileUpload]: FileUploadDetails,
};

export const NotificationDetailsPanel = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const notificationId = searchParams.get("notificationId");

  const { data, isLoading } = useNotificationsQuery(
    { id: notificationId || "" },
    { enabled: !!notificationId }
  );

  const { data: autotaskData } = useAutotaskConnectionQuery();
  const apiBaseUrl = autotaskData?.autotaskConnection?.apiBaseUrl;

  const autotaskWebUrl = useMemo(() => {
    let baseUrl = "https://ww2.autotask.net";
    if (apiBaseUrl) {
      try {
        const url = new URL(apiBaseUrl);
        const webHostname = url.hostname.replace("webservices", "ww");
        baseUrl = `https://${webHostname}`;
      } catch {
        // Fallback already set
      }
    }
    return baseUrl;
  }, [apiBaseUrl]);

  const notification = data?.notifications?.edges?.[0]?.node;

  const { resolveNotification } = useNotification();

  const handleClose = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete("notificationId");
      return prev;
    });
  }, [setSearchParams]);

  const handleResolve = useCallback(async () => {
    if (notificationId) {
      await resolveNotification(notificationId);
      handleClose();
    }
  }, [notificationId, resolveNotification, handleClose]);

  const DetailsComponent = notification?.notificationType
    ? detailsMap[notification.notificationType as NotificationEnum]
    : null;

  return (
    <SlideInPanel
      isOpen={!!notificationId}
      onClose={handleClose}
      onCancel={handleClose}
      cancelButtonText="Close"
      onApply={notification && !notification.resolvedAt ? handleResolve : undefined}
      applyButtonText={notification && !notification.resolvedAt ? "Resolve Notification" : undefined}
      title="Notification Details"
    >
      <Box direction="column" gap={24} padding="lg">
        {isLoading && <Text>Loading...</Text>}
        {!isLoading && !notification && <Text>Notification not found.</Text>}
        {!isLoading && notification && (
          <>
            <Box direction="column" gap={4}>
              <Text size={12} color="textSecondary" weight={600} uppercase>
                Type
              </Text>
              <NotificationTypeMappingText
                notificationType={notification.notificationType as NotificationEnum}
              />
            </Box>

            {DetailsComponent && <DetailsComponent notification={notification} />}

            {(notification.autotaskTicketNumber || notification.autotaskTicketId) && (
              <Box direction="column" gap={4}>
                <Text size={12} color="textSecondary" weight={600} uppercase>
                  Autotask Ticket
                </Text>
                {notification.autotaskTicketNumber ? (
                  <a
                    href={`${autotaskWebUrl}/Autotask/AutotaskExtend/ExecuteCommand.aspx?Code=OpenTicketDetail&TicketNumber=${notification.autotaskTicketNumber}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "var(--color-primary-500)", textDecoration: "none" }}
                  >
                    View Ticket {notification.autotaskTicketNumber}
                  </a>
                ) : (
                  <a
                    href={`${autotaskWebUrl}/Mvc/ServiceDesk/TicketDetail.mvc?workspace=False&ticketId=${notification.autotaskTicketId}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "var(--color-primary-500)", textDecoration: "none" }}
                  >
                    View Ticket #{notification.autotaskTicketId}
                  </a>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </SlideInPanel>
  );
};
